import type { ComponentWidgetConfig, WidgetType } from './base-product-tile-types.js'

interface HiddenFieldFlag {
  key: string
  field: string
  hiddenWhen: boolean
}

const RENAMED_PROPERTIES: Partial<Record<WidgetType, Record<string, string>>> = {
  'csc-brand': { variant: 'layout' },
  'csc-retail-price': { variant: 'fontVariant' },
  'csc-plp-cta-button': { variant: 'type' },
  'csc-product-tile-variants': { maxVariantsAmount: 'displayLimit' },
  'csc-price': { variant: 'priceVariant', vatDisplay: 'vatMode', price1VatDisplay: 'price1VatMode', price2VatDisplay: 'price2VatMode' },
}

const HIDDEN_FIELD_FLAGS: Partial<Record<WidgetType, HiddenFieldFlag[]>> = {
  'csc-product-tile-product-code': [{ key: 'hideText', field: 'label', hiddenWhen: true }],
  'csc-order-form': [{ key: 'hideStepper', field: 'stepper', hiddenWhen: true }],
  'csc-price': [
    { key: 'hideUom', field: 'uom', hiddenWhen: true },
    { key: 'showVat', field: 'vat', hiddenWhen: false },
    { key: 'showPrice1', field: 'price1', hiddenWhen: false },
  ],
}

const toCamelCase = (key: string) => key.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase())

const toBoolean = (value: unknown) => value === true || value === 'true'

const parseHiddenFields = (value: unknown): string[] =>
  typeof value === 'string' ? value.split(',').map(entry => entry.trim()).filter(Boolean) : []

const withHiddenField = (properties: ComponentWidgetConfig, field: string): ComponentWidgetConfig => {
  const fields = new Set(parseHiddenFields(properties['hiddenFields']))
  fields.add(field)
  return { ...properties, hiddenFields: [...fields].join(',') }
}

const migratePriceSecondSlot = (properties: ComponentWidgetConfig): ComponentWidgetConfig => {
  if (!('showPrice2' in properties)) return properties
  const { showPrice2, ...rest } = properties
  if (!toBoolean(showPrice2)) {
    return Object.fromEntries(Object.entries(rest).filter(([key]) => key !== 'price2Source'))
  }
  return { ...rest, price2Source: rest['price2Source'] || 'consumer' }
}

const STOCK_INDICATION_VARIANT_OF_COMPOSITION: Record<string, string> = {
  bordered: 'default',
  simple: 'simple',
  solid: 'solid',
  floating: 'floating',
}

const migrateStockIndicationComposition = (properties: ComponentWidgetConfig): ComponentWidgetConfig => {
  const hiddenFields = parseHiddenFields(properties['hiddenFields'])
  const hidesText = hiddenFields.includes('text')
  if (!('composition' in properties) && !hidesText) return properties

  const { composition, ...withoutComposition } = properties
  const rest = Object.fromEntries(Object.entries(withoutComposition).filter(([key]) => key !== 'hiddenFields'))
  const remainingHiddenFields = hiddenFields.filter(field => field !== 'text')
  const variant = hidesText ? 'minimal' : STOCK_INDICATION_VARIANT_OF_COMPOSITION[composition] ?? rest['variant']
  return {
    ...rest,
    ...(variant && { variant }),
    ...(remainingHiddenFields.length && { hiddenFields: remainingHiddenFields.join(',') }),
  }
}

const hasMigrations = (type: WidgetType) =>
  type in RENAMED_PROPERTIES || type in HIDDEN_FIELD_FLAGS || type === 'csc-stock-indication'

export const renameDeprecatedWidgetProperties = (type: WidgetType, properties: ComponentWidgetConfig = {}): ComponentWidgetConfig => {
  if (!hasMigrations(type)) return properties

  const renames = RENAMED_PROPERTIES[type] ?? {}
  let migrated: ComponentWidgetConfig = Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      const camelKey = toCamelCase(key)
      return [renames[camelKey] ?? camelKey, value]
    }),
  )

  if (type === 'csc-price') migrated = migratePriceSecondSlot(migrated)
  if (type === 'csc-stock-indication') migrated = migrateStockIndicationComposition(migrated)

  for (const { key, field, hiddenWhen } of HIDDEN_FIELD_FLAGS[type] ?? []) {
    if (!(key in migrated)) continue
    const { [key]: value, ...rest } = migrated
    migrated = toBoolean(value) === hiddenWhen ? withHiddenField(rest, field) : rest
  }

  return migrated
}
